import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { ApiError } from './api/client';
import Ornament from './components/Ornament';
import Icon from './components/Icon';
import './Enroll.css';

const EMPTY = {
    slug: '', name: '', fullName: '',
    orgType: 'organisation',
    foundedYear: '',
    about: '', sampradaya: '', parampara: '',
    website: '', logoUrl: '',
    isPublic: true,
    primaryContact: { name: '', role: '', email: '', phone: '' },
    taxId: '', registrationNumber: '',
    hiringNeeds: [],
    compensationRange: { min: '', max: '', currency: 'INR' },
    expectedHiresPerYear: '',
    allowExternalApps: true,
    accepts_terms: false,
};

const ORG_TYPES = [
    'Temple', 'Maṭha', 'Pāṭhaśālā', 'University / Academic department',
    'Trust / NGO', 'Dharmik Business', 'Family office', 'Individual employer',
    'Multi-branch umbrella', 'Other',
];
const HIRING_PILLS = [
    'Full-time', 'Part-time', 'Contract', 'Event-based',
    'Volunteer', 'Internship', 'Ṛtvik / officiant', 'Teaching', 'Administrative',
];

const EnrollCollaborator = () => {
    const [form, setForm] = useState(EMPTY);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        if (!localStorage.getItem('token')) {
            navigate('/signin?next=' + encodeURIComponent('/enroll/collaborator'));
        }
    }, [navigate]);

    const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
    const setContact = (k, v) => setForm((p) => ({ ...p, primaryContact: { ...p.primaryContact, [k]: v } }));
    const setComp    = (k, v) => setForm((p) => ({ ...p, compensationRange: { ...p.compensationRange, [k]: v } }));
    const togglePill = (k, value) => setForm((p) => ({
        ...p,
        [k]: p[k].includes(value) ? p[k].filter((x) => x !== value) : [...p[k], value],
    }));

    // Auto-suggest slug from the org name
    const onNameChange = (e) => {
        const name = e.target.value;
        setForm((p) => {
            const autoSlug = !p.slug || p.slug === slugify(p.name)
                ? slugify(name) : p.slug;
            return { ...p, name, slug: autoSlug };
        });
    };
    const slugify = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);

    const submit = async (e) => {
        e.preventDefault();
        setError(''); setSuccess('');
        if (!form.accepts_terms) { setError('Please accept the terms.'); return; }
        if (!form.slug || !form.name) { setError('Slug and name are required.'); return; }
        if (!/^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(form.slug)) {
            setError('Slug must be lowercase, hyphenated, 3–60 chars.');
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                foundedYear: form.foundedYear ? parseInt(form.foundedYear, 10) : null,
                expectedHiresPerYear: form.expectedHiresPerYear ? parseInt(form.expectedHiresPerYear, 10) : null,
                compensationRange: {
                    min: form.compensationRange.min ? parseFloat(form.compensationRange.min) : null,
                    max: form.compensationRange.max ? parseFloat(form.compensationRange.max) : null,
                    currency: form.compensationRange.currency,
                },
            };
            const data = await api.post('/api/enroll/collaborator', payload);
            setSuccess(`Submitted for verification. Slug: ${data.organisation.slug}. Our team will review within 2–3 working days.`);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (err) {
            setError(err instanceof ApiError ? err.message : 'Failed to submit. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="enroll-page">
            <div className="enroll-shell">

                <header className="enroll-header">
                    <Ornament variant="rule" tone="gold" size={120} style={{ margin: '0 auto 12px', display: 'block' }} />
                    <span className="enroll-eyebrow">Collaborate · Enrol your institution</span>
                    <h1>Bring your organisation onto Samudyoga</h1>
                    <p>
                        Captures everything we need to verify and onboard your institution.
                        After review, you'll be activated as a Central Admin and can post openings,
                        invite branch admins, and manage applications.
                    </p>
                </header>

                <form className="enroll-form" onSubmit={submit}>

                    {/* 1. Organisation basics */}
                    <div className="enroll-section">
                        <div className="enroll-section-title">
                            <span className="enroll-section-num">1</span>
                            <h2>Organisation basics</h2>
                        </div>
                        <div className="enroll-row">
                            <div className="enroll-field">
                                <label>Name *</label>
                                <input type="text" value={form.name} onChange={onNameChange}
                                       placeholder="Śrī ... Pīṭham" required />
                            </div>
                            <div className="enroll-field">
                                <label>URL slug * <span className="hint">(lowercase, hyphenated)</span></label>
                                <input type="text" value={form.slug}
                                       onChange={(e) => set('slug', e.target.value.toLowerCase())}
                                       placeholder="sri-xxx-pitham" required />
                            </div>
                        </div>
                        <div className="enroll-row">
                            <div className="enroll-field">
                                <label>Full / formal name</label>
                                <input type="text" value={form.fullName}
                                       onChange={(e) => set('fullName', e.target.value)}
                                       placeholder="Śrī ... Mahā-saṁsthānam Pīṭham" />
                            </div>
                            <div className="enroll-field">
                                <label>Type</label>
                                <select value={form.orgType} onChange={(e) => set('orgType', e.target.value)}>
                                    {ORG_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="enroll-row">
                            <div className="enroll-field">
                                <label>Founded (year)</label>
                                <input type="number" min="0" max="2099" value={form.foundedYear}
                                       onChange={(e) => set('foundedYear', e.target.value)} placeholder="1895" />
                            </div>
                            <div className="enroll-field">
                                <label>Website</label>
                                <input type="url" value={form.website}
                                       onChange={(e) => set('website', e.target.value)}
                                       placeholder="https://..." />
                            </div>
                        </div>
                        <div className="enroll-field">
                            <label>About</label>
                            <textarea value={form.about} onChange={(e) => set('about', e.target.value)}
                                      placeholder="History, paramparā, mission, scale of operations…" />
                        </div>
                        <div className="enroll-row">
                            <div className="enroll-field">
                                <label>Sampradāya focus <span className="hint">(optional)</span></label>
                                <input type="text" value={form.sampradaya}
                                       onChange={(e) => set('sampradaya', e.target.value)}
                                       placeholder="Smārta, Śrī-vaiṣṇava, Mādhva… or 'Open to all'" />
                            </div>
                            <div className="enroll-field">
                                <label>Paramparā <span className="hint">(optional)</span></label>
                                <input type="text" value={form.parampara}
                                       onChange={(e) => set('parampara', e.target.value)}
                                       placeholder="Guru-paramparā" />
                            </div>
                        </div>
                        <div className="enroll-field">
                            <label>Logo URL <span className="hint">(optional)</span></label>
                            <input type="url" value={form.logoUrl}
                                   onChange={(e) => set('logoUrl', e.target.value)}
                                   placeholder="https://… (square PNG / SVG)" />
                        </div>
                    </div>

                    {/* 2. Verification documents */}
                    <div className="enroll-section">
                        <div className="enroll-section-title">
                            <span className="enroll-section-num">2</span>
                            <h2>Verification</h2>
                        </div>
                        <p className="enroll-section-hint">
                            Used by our team to verify your organisation's identity. Stored privately, never shown publicly.
                        </p>
                        <div className="enroll-row">
                            <div className="enroll-field">
                                <label>Registration number</label>
                                <input type="text" value={form.registrationNumber}
                                       onChange={(e) => set('registrationNumber', e.target.value)}
                                       placeholder="Trust / NGO / Society / Company registration" />
                            </div>
                            <div className="enroll-field">
                                <label>Tax ID</label>
                                <input type="text" value={form.taxId}
                                       onChange={(e) => set('taxId', e.target.value)}
                                       placeholder="PAN / GST / EIN" />
                            </div>
                        </div>
                    </div>

                    {/* 3. Primary contact */}
                    <div className="enroll-section">
                        <div className="enroll-section-title">
                            <span className="enroll-section-num">3</span>
                            <h2>Primary contact</h2>
                        </div>
                        <div className="enroll-row">
                            <div className="enroll-field">
                                <label>Name *</label>
                                <input type="text" value={form.primaryContact.name}
                                       onChange={(e) => setContact('name', e.target.value)} required />
                            </div>
                            <div className="enroll-field">
                                <label>Role</label>
                                <input type="text" value={form.primaryContact.role}
                                       onChange={(e) => setContact('role', e.target.value)}
                                       placeholder="Principal / President / HR Head" />
                            </div>
                        </div>
                        <div className="enroll-row">
                            <div className="enroll-field">
                                <label>Email *</label>
                                <input type="email" value={form.primaryContact.email}
                                       onChange={(e) => setContact('email', e.target.value)} required />
                            </div>
                            <div className="enroll-field">
                                <label>Phone</label>
                                <input type="tel" value={form.primaryContact.phone}
                                       onChange={(e) => setContact('phone', e.target.value)} />
                            </div>
                        </div>
                    </div>

                    {/* 4. Hiring needs */}
                    <div className="enroll-section">
                        <div className="enroll-section-title">
                            <span className="enroll-section-num">4</span>
                            <h2>Hiring needs</h2>
                        </div>
                        <div className="enroll-field">
                            <label>What you'll be hiring for <span className="hint">(select all that apply)</span></label>
                            <div className="enroll-pill-group">
                                {HIRING_PILLS.map((t) => (
                                    <button type="button" key={t}
                                            className={`enroll-pill ${form.hiringNeeds.includes(t) ? 'selected' : ''}`}
                                            onClick={() => togglePill('hiringNeeds', t)}>
                                        {t}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="enroll-row-3">
                            <div className="enroll-field">
                                <label>Comp. min</label>
                                <input type="number" min="0" value={form.compensationRange.min}
                                       onChange={(e) => setComp('min', e.target.value)} />
                            </div>
                            <div className="enroll-field">
                                <label>Comp. max</label>
                                <input type="number" min="0" value={form.compensationRange.max}
                                       onChange={(e) => setComp('max', e.target.value)} />
                            </div>
                            <div className="enroll-field">
                                <label>Currency</label>
                                <select value={form.compensationRange.currency}
                                        onChange={(e) => setComp('currency', e.target.value)}>
                                    <option value="INR">INR</option>
                                    <option value="USD">USD</option>
                                    <option value="GBP">GBP</option>
                                    <option value="EUR">EUR</option>
                                    <option value="AED">AED</option>
                                    <option value="SGD">SGD</option>
                                </select>
                            </div>
                        </div>
                        <div className="enroll-field">
                            <label>Expected hires per year</label>
                            <input type="number" min="0" value={form.expectedHiresPerYear}
                                   onChange={(e) => set('expectedHiresPerYear', e.target.value)} />
                        </div>
                    </div>

                    {/* 5. Visibility & access */}
                    <div className="enroll-section">
                        <div className="enroll-section-title">
                            <span className="enroll-section-num">5</span>
                            <h2>Visibility &amp; access</h2>
                        </div>
                        <div className="enroll-checkbox-row">
                            <input type="checkbox" id="is_public" checked={form.isPublic}
                                   onChange={(e) => set('isPublic', e.target.checked)} />
                            <label htmlFor="is_public">
                                Show our public organisation page in the Samudyoga directory.
                                <em> Postings remain individually controllable as internal or public.</em>
                            </label>
                        </div>
                        <div className="enroll-checkbox-row">
                            <input type="checkbox" id="allow_ext" checked={form.allowExternalApps}
                                   onChange={(e) => set('allowExternalApps', e.target.checked)} />
                            <label htmlFor="allow_ext">
                                Allow external (non-member) candidates to apply, by default.
                                You can override this on each posting.
                            </label>
                        </div>
                    </div>

                    <div className="enroll-checkbox-row">
                        <input type="checkbox" id="terms" checked={form.accepts_terms}
                               onChange={(e) => set('accepts_terms', e.target.checked)} required />
                        <label htmlFor="terms">
                            I am authorised to register this organisation. I accept the Samudyoga
                            <a href="#" style={{ marginLeft: 4 }}>Terms</a> and <a href="#">Code of Conduct</a>.
                        </label>
                    </div>

                    {success && <div className="enroll-success"><Icon name="check" size={16} /> {success}</div>}
                    {error   && <div className="enroll-error">{error}</div>}

                    <div className="enroll-submit-row">
                        <span className="left">Verification typically takes 2–3 working days.</span>
                        <div className="right">
                            <button type="button" className="sw-btn sw-btn-ghost" onClick={() => navigate('/collaborate')}>
                                Cancel
                            </button>
                            <button type="submit" className="sw-btn sw-btn-saffron sw-btn-lg" disabled={submitting}>
                                {submitting ? 'Submitting…' : 'Submit for verification'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EnrollCollaborator;
